import {
	DEFAULT_DESTINATION_TYPE,
	DESTINATION_TYPE_TABLE,
	EXPLORE_TYPE_TO_DESTINATION_TYPE,
	getSelectedColor,
	type DestinationType,
} from "components/map/markerVisualTokens";
import { getTransportLineLayout, getTransportLinePaint } from "components/map/utils/transportLineStyles";
import { curvedOrStraightLine, type LonLat } from "utils/curveLine";
import { pointOfStop, type MapPoint, type RouteStopData } from "./data";
import { km, type LatLng } from "./plannerLogic";

/**
 * What the route map draws, as pure functions: which marker a destination type wears, how big it is at a zoom, which name plates
 * show and where, the camera's frames, and the line through a route's stops. The marker values are design/map-markers (markers.md,
 * states.md, transport-lines.md) as Ilse's v2 demo (maps-v2, country_body.html) draws them; the colours and the line come from the
 * same tokens the app's map reads. Lives apart from data.ts because it pulls those tokens in: only the map's chunk loads it.
 */

// The destination cards' own kinds, and a few the explore types do not name, so a point typed by its card's kind still finds its marker.
const TYPE_BY_NAME: Record<string, DestinationType> = {
	...Object.fromEntries(Object.entries(EXPLORE_TYPE_TO_DESTINATION_TYPE).map(([name, type]) => [name.toLowerCase(), type])),
	capital: "city",
	"mountain town": "town",
	coast: "island-beach",
	waterfall: "natural-feature",
	ruins: "natural-feature",
	historic: "heritage-site",
};

/** A type nothing resolves is drawn as a region, as the app's map does for a destination it has no type for. */
export function destinationTypeOf(raw: string | null | undefined): DestinationType {
	const name = String(raw ?? "")
		.trim()
		.replace(/^["']+|["']+$/g, "")
		.trim()
		.toLowerCase();
	return TYPE_BY_NAME[name] ?? DEFAULT_DESTINATION_TYPE;
}

/** a key of partners/shared/markerIcons.json */
const ICON_OF_TYPE: Record<DestinationType, string> = {
	city: "city",
	town: "town",
	region: "region",
	"national-park": "tree",
	"island-beach": "island",
	"natural-feature": "mountain",
	"heritage-site": "castle",
	"ski-resort": "mountain",
};

export interface MarkerLook {
	/** the light tint of the marker; its icon is black */
	tint: string;
	/** the saturated version of it: the dot, and the rim of a selected marker */
	dot: string;
	icon: string;
	/** the dark ink of the type, for the words on its tint (the place card's chip) */
	ink: string;
}

export function markerLook(raw: string | null | undefined): MarkerLook {
	const type = destinationTypeOf(raw);
	const tint = DESTINATION_TYPE_TABLE[type].fill;
	return { tint, dot: getSelectedColor(tint), icon: ICON_OF_TYPE[type], ink: DESTINATION_TYPE_TABLE[type].iconInk };
}

// ---- the zoom ladder ----------------------------------------------------------------------------------------
// The demo's thresholds as they are: its base map is drawn on 512px tiles at its own zoom, like MapLibre's, so one zoom is one zoom
// and a ground that is 7.4 in the demo is 7.4 here. (A narrower map is not read as a higher zoom: a phone shows the country at a
// lower zoom than a desktop, as it does in the demo.)

/** Below this zoom a destination is a dot, and only the route's stops keep their marker. */
export const FAR_BELOW_ZOOM = 5.2;
/** From this zoom a route's stops are photo cards. */
export const STOP_PHOTO_FROM_ZOOM = 6.6;
const STOP_PHOTO_BELOW_ZOOM = 13;
/** From this zoom every destination carries its name plate. */
export const NEAR_FROM_ZOOM = 7.4;

export const isFar = (zoom: number): boolean => zoom < FAR_BELOW_ZOOM;

/** Below this zoom a route whose stops are far enough apart still gets photo cards (the demo's `zph`) */
const ROOMY_PHOTO_FROM_ZOOM = 5.6;
/** how far apart (px) the closest two stops must be for the photo cards of a route to have room below STOP_PHOTO_FROM_ZOOM */
const ROOMY_GAP_PX = 80;

/**
 * Whether the route's stops are photo cards at this zoom: from z6.6, or from z5.6 when the closest two stops are 80px apart (so a long route
 * shows photos before it is zoomed in). `gap` is that closest distance on screen; Infinity for fewer than two stops.
 */
export const stopPhotosAt = (zoom: number, gap: number): boolean =>
	showsStopPhotos(zoom) || (Number.isFinite(gap) && gap >= ROOMY_GAP_PX && zoom >= ROOMY_PHOTO_FROM_ZOOM && zoom < STOP_PHOTO_BELOW_ZOOM);

/** The closest two of these points on screen, px; Infinity when there are fewer than two */
export function closestGap(points: { x: number; y: number }[]): number {
	let gap = Infinity;
	for (let i = 0; i < points.length; i++)
		for (let k = i + 1; k < points.length; k++) gap = Math.min(gap, Math.hypot(points[i].x - points[k].x, points[i].y - points[k].y));
	return gap;
}

/**
 * Which stops keep their photo card: in route order, a card that would cover a card already placed, or sit on top of another stop, stays a
 * numbered disc (the demo's `nophoto`). `r` is the marker radius at this zoom; a card is 3.9 r across.
 */
export function photoCards(stops: { id: string; x: number; y: number }[], r: number): Set<string> {
	const card = r * 3.9;
	const shown: { x: number; y: number }[] = [];
	const ids = new Set<string>();
	for (const p of stops) {
		const hit =
			shown.some((q) => Math.abs(q.x - p.x) < card && Math.abs(q.y - p.y) < card + 18) ||
			stops.some((o) => o !== p && Math.abs(o.x - p.x) < card / 2 + r * 0.7 && Math.abs(o.y - p.y) < card / 2 + r * 0.7 + 8);
		if (!hit) {
			shown.push(p);
			ids.add(p.id);
		}
	}
	return ids;
}
export const showsStopPhotos = (zoom: number): boolean => zoom >= STOP_PHOTO_FROM_ZOOM && zoom < STOP_PHOTO_BELOW_ZOOM;
export const isNear = (zoom: number): boolean => zoom >= NEAR_FROM_ZOOM;

/** A destination's radius in px: 15 below z6, 19 below z7.2, 23 above (markers.md) */
export const markerRadius = (zoom: number): number => (zoom < 6 ? 15 : zoom < 7.2 ? 19 : 23);

// ---- name plates ---------------------------------------------------------------------------------------------

/** A plate's height, and what its width is made of: a character is about 7.4px of 12.5px Rubik, and the padding is 7px a side (the demo's placePlates). */
const PLATE_HEIGHT = 21;
const plateWidth = (label: string): number => label.length * 7.4 + 16;
/** The room a pin takes, as a radius: a photo card on a stop is R * 3.9 across, a far dot 14px. */
export const pinRadius = ({ r, far, stop, photo }: { r: number; far: boolean; stop: boolean; photo: boolean }): number =>
	photo ? r * 1.95 : far && !stop ? 7 : r;

/**
 * Whose name plate has the say: the pin that is open or under the mouse (0), then the route's stops in order, then the must-sees, then
 * the rest. Below 3 a plate is shown at any zoom; the rest wait for `near`.
 */
export const pinPriority = ({ active, stopNumber, must }: { active: boolean; stopNumber: number; must: boolean }): number =>
	active ? 0 : stopNumber ? 1 + stopNumber / 100 : must ? 2 : 3;

export interface PlateInput {
	id: string;
	/** where the map puts the pin on screen, px */
	x: number;
	y: number;
	/** what the plate says, nights included ("Chiang Mai · 2n"): its length is its width */
	label: string;
	priority: number;
	radius: number;
}

export type PlateSide = "below" | "above" | "hidden";

/**
 * Where each pin's name plate goes, highest priority first: below its pin, else above it, else none; a plate that would cover a plate
 * or a pin that is already placed moves or hides. The rest of the pins (priority 3) show their plates only when the map is `near`.
 * `onlyActive`: the overview of the country, where no pin has a name until it is under the mouse or opened.
 */
export function placePlates(pins: PlateInput[], near: boolean, onlyActive = false): Map<string, PlateSide> {
	type Box = { x: number; y: number; w: number; h: number };
	const placed: Box[] = [];
	const hit = (r: Box) => placed.some((q) => r.x < q.x + q.w && r.x + r.w > q.x && r.y < q.y + q.h && r.y + r.h > q.y);
	const sides = new Map<string, PlateSide>();
	// a stable sort: pins of one priority keep their order
	for (const p of [...pins].sort((a, b) => a.priority - b.priority)) {
		// the first view, zoomed out: no name but the one the mouse is on (the demo's `ov`)
		if (onlyActive ? p.priority !== 0 : !near && p.priority >= 3) {
			sides.set(p.id, "hidden");
			continue;
		}
		const w = plateWidth(p.label);
		const below: Box = { x: p.x - w / 2, y: p.y + p.radius + 1, w, h: PLATE_HEIGHT };
		const above: Box = { x: p.x - w / 2, y: p.y - p.radius - 3 - PLATE_HEIGHT, w, h: PLATE_HEIGHT };
		const body: Box = { x: p.x - p.radius, y: p.y - p.radius, w: p.radius * 2, h: p.radius * 2 };
		if (!hit(below)) {
			placed.push(below, body);
			sides.set(p.id, "below");
		} else if (!hit(above)) {
			placed.push(above, body);
			sides.set(p.id, "above");
		} else {
			placed.push(body);
			sides.set(p.id, "hidden");
		}
	}
	return sides;
}

// ---- the pins ------------------------------------------------------------------------------------------------

export interface PinModel {
	/** the destination's slug; `stop-<n>` for a stop of the route that is no destination of the guide (Isan) */
	id: string;
	/** the destination, when there is one: it brings the photo, the type and the card */
	point: MapPoint | null;
	name: string;
	lat: number;
	lng: number;
	/** the destination's photo, else the stop's own (a stop that is no destination of the guide); "" when there is none */
	photo: string;
	/** the open route's number and nights here, when it stops here */
	stop: { n: number; nights: number } | null;
}

/**
 * What the map draws: a pin for every destination of the guide, and for a stop of the route that is none a pin of its own. A destination
 * the route stops at is its stop (a navy disc with the number), not a second marker.
 */
export function buildPins(points: MapPoint[], stops: RouteStopData[]): PinModel[] {
	const bySlug = new Map<string, { n: number; nights: number }>();
	const extra: PinModel[] = [];
	for (const { n, stop, nights } of numberedStops(stops)) {
		const point = pointOfStop(stop, points);
		if (point && !bySlug.has(point.slug)) bySlug.set(point.slug, { n, nights });
		else extra.push({ id: `stop-${n}`, point: null, name: stop.name, lat: stop.lat, lng: stop.lng, photo: stop.photo ?? "", stop: { n, nights } });
	}
	return [...points.map((p) => ({ id: p.slug, point: p, name: p.name, lat: p.lat, lng: p.lng, photo: p.photo, stop: bySlug.get(p.slug) ?? null })), ...extra];
}

// ---- the route -----------------------------------------------------------------------------------------------

/** Two stops this close are one place. */
const SAME_SPOT_KM = 1;

/** The route's stops with the same place twice in a row (two legs from one base) once. */
const visitsOf = (stops: RouteStopData[]): RouteStopData[] => stops.filter((s, i) => i === 0 || km(stops[i - 1], s) >= SAME_SPOT_KM);

/**
 * The stops that get a numbered disc, with their numbers: a place the route comes back to (Bangkok first and last) keeps its first
 * number only, because a second disc would sit on the first. `nights` is what the disc's name plate says ("Chiang Mai · 2n"): every
 * night the route sleeps in that place, its visits added up.
 * shortcut: the return visit has no number of its own, give the disc "1, 5" when a route that doubles back is worth reading from the map
 */
export function numberedStops(stops: RouteStopData[]): { n: number; stop: RouteStopData; nights: number }[] {
	const discs: { n: number; stop: RouteStopData; nights: number }[] = [];
	visitsOf(stops).forEach((stop, i) => {
		if (!discs.some((d) => km(d.stop, stop) < SAME_SPOT_KM)) discs.push({ n: i + 1, stop, nights: 0 });
	});
	for (const stop of stops) {
		const disc = discs.find((d) => km(d.stop, stop) < SAME_SPOT_KM);
		if (disc) disc.nights += stop.nights;
	}
	return discs;
}

/** The line through the stops in order, one slightly curved leg per pair (a lone leg stays straight), in the "nothing chosen" style. */
export function routeLine(stops: RouteStopData[]): GeoJSON.FeatureCollection<GeoJSON.LineString> {
	const points = visitsOf(stops).map((s): LonLat => [s.lng, s.lat]);
	const legs = points.length - 1;
	return { type: "FeatureCollection", features: points.slice(1).map((end, i) => curvedOrStraightLine(points[i], end, i, legs)) };
}

export const ROUTE_LINE_PAINT = getTransportLinePaint("nothingChosen");
export const ROUTE_LINE_LAYOUT = getTransportLineLayout("nothingChosen");

// ---- the camera ----------------------------------------------------------------------------------------------

/** The zoom the demo's pixel distances are measured at (its base map's), and so what "70px" and "160px" below mean. */
const DEMO_ZOOM = 5.83;
/** A route or the open tab is framed no closer than the demo's scale 3, and a place no closer than its scale 3.4. */
export const FRAME_MAX_ZOOM = DEMO_ZOOM + Math.log2(3);
export const PLACE_MAX_ZOOM = DEMO_ZOOM + Math.log2(3.4);
/** How near a destination is to the open place, and to a lone stop, for the camera to bring it into the frame (the demo's 70px and 160px). */
export const NEAR_PLACE_PX = 70;
export const NEAR_STOP_PX = 160;

const demoPx = ({ lat, lng }: { lat: number; lng: number }): [number, number] => {
	const world = 512 * 2 ** DEMO_ZOOM;
	const sin = Math.sin((lat * Math.PI) / 180);
	return [((lng + 180) / 360) * world, (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * world];
};

/** The points within `px` (at the demo's zoom) of `center`, `center` itself not among them. */
export function pointsNear<T extends LatLng>(center: LatLng, points: T[], px: number): T[] {
	const [cx, cy] = demoPx(center);
	return points.filter((p) => {
		if ((p as LatLng) === center) return false;
		const [x, y] = demoPx(p);
		return Math.hypot(x - cx, y - cy) < px;
	});
}

/** What the camera frames for the tab that is open: the stops of the route, a lone stop with the destinations around it, else every destination. */
export function frameOf(stops: RouteStopData[], points: MapPoint[]): LonLat[] {
	if (stops.length > 1) return stops.map((s) => [s.lng, s.lat]);
	const around = stops.length === 1 ? [stops[0], ...pointsNear(stops[0], points, NEAR_STOP_PX)] : points;
	return around.map((p) => [p.lng, p.lat]);
}

export interface Box {
	x: number;
	y: number;
	w: number;
	h: number;
}

/**
 * The part of the map a card leaves free: what is above it (below it, for the pointer card), or what is beside it, whichever is larger (the demo's fitVisible).
 * Both boxes are in the map's own coordinates, and `sheet` is null while there is no card on the map.
 */
export function freeRegion(map: Box, sheet: Box | null, pointer = false, aspect?: number): Box {
	const cut = sheet && sheet.x < map.x + map.w && sheet.x + sheet.w > map.x && sheet.y < map.y + map.h && sheet.y + sheet.h > map.y ? sheet : null;
	if (!cut) return map;
	const beside: Box = { x: cut.x + cut.w, y: map.y, w: Math.max(0, map.x + map.w - (cut.x + cut.w)), h: map.h };
	// the place card has what is above it or beside it; the pointer card (the first view's, level with the list) has what is below it or beside it
	const over: Box = pointer
		? { x: map.x, y: cut.y + cut.h, w: map.w, h: Math.max(0, map.y + map.h - (cut.y + cut.h)) }
		: { x: map.x, y: map.y, w: map.w, h: Math.max(0, cut.y - map.y) };
	// With the shape of what is framed (its width over its height on screen), the region it is drawn largest in: a route as tall as
	// Thailand's is bigger in the column beside the pointer card than in the strip under it. Without a shape, the larger region.
	const room = (r: Box) => (aspect && Number.isFinite(aspect) ? Math.min(r.w, r.h * aspect) : r.w * r.h);
	return room(over) >= room(beside) ? over : beside;
}

/** The room kept at the side of a column of the map beside the pointer card, as a share of its width, and its least: half a photo card (37px) and a bit */
const COLUMN_SIDE = 0.14;
const COLUMN_SIDE_MIN = 40;

/**
 * `fitBounds` padding that keeps a frame inside `region`, with the room the legend takes above it (78px; 58px on a phone, under the "Open in app" chip) and the name plates below.
 * `pointer`: the region is what the first view's pointer card leaves. The column beside that card is about 300px wide, and 90px at each of its sides left a
 * route 126px: a route 3.5 to 4.6 degrees wide (Vietnam's long ones) was framed by that width, 0.3 to 0.8 of a zoom below what its height allows. A narrow
 * column keeps less of the room at its sides (plates lean in at the map's edges); the region of a whole map keeps its 90.
 */
export function fitPadding(map: Box, region: Box, phone: boolean, pointer = false) {
	const side = Math.min(90, map.w * 0.16, pointer && region.w < map.w ? Math.max(COLUMN_SIDE_MIN, region.w * COLUMN_SIDE) : Infinity);
	return {
		top: region.y - map.y + (phone ? 58 : 78),
		bottom: map.y + map.h - (region.y + region.h) + (phone ? 30 : 44),
		left: region.x - map.x + side,
		right: map.x + map.w - (region.x + region.w) + side,
	};
}
